import { SurveyRespond } from "@/pages/employees/surveys/SurveyRespond.tsx";
import { GoalDetails } from "@/pages/talent/goals/details/GoalDetails.tsx";
import { InitiativesGoalsPage } from "@/pages/talent/goals/InitiativesGoalsPage.tsx";
import SearchGoalsPage from "@/pages/talent/goals/SearchGoalsPage.tsx";
import { StrategicGoalPage } from "@/pages/talent/goals/StrategicGoalPage.tsx";
import { StrategicGoalsPage } from "@/pages/talent/goals/StrategicGoalsPage.tsx";
import { SurveyEditPage } from "@/pages/talent/surveys/edit/SurveyEditPage.tsx";
import { SurveyHistoryPage } from "@/pages/talent/surveys/SurveyHistoryPage.tsx";
import { SurveyRequestsPage } from "@/pages/talent/surveys/SurveyRequestsPage.tsx";
import { SurveysIndex } from "@/pages/talent/surveys/SurveysIndex.tsx";
import { SurveyTemplateEditPage } from "@/pages/talent/surveys/templates/edit/SurveyTemplateEditPage.tsx";
import { SurveysView } from "@/pages/talent/surveys/view/SurveyViewPage.tsx";
import { RouteConfig } from "@/routes/types/route.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { PerformanceReviewsIndex } from "@/pages/talent/performance-reviews/PerformanceReviewsIndex.tsx";
import { PerformanceReviewViewPage } from "@/pages/talent/performance-reviews/view/PerformanceReviewViewPage.tsx";
import { PerformanceReviewParticipantReport } from "@/pages/talent/performance-reviews/view/PerformanceReviewParticipantReport.tsx";
import { PerformanceReviewEditPage } from "@/pages/talent/performance-reviews/edit/PerformanceReviewEditPage.tsx";
import { PerformanceReviewRespond } from "@/pages/employees/performance-reviews/PerformanceReviewRespond.tsx";
import { PerformanceReviewTemplateEditPage } from "@/pages/talent/performance-reviews/templates/edit/PerformanceReviewTemplateEditPage.tsx";
import { MyFeedbackIndex } from "@/pages/talent/my-feedback/MyFeedbackIndex.tsx";
import {
  PerformanceReviewTemplatesIndex,
  SurveyTemplatesIndex,
  TemplatesIndexRedirect,
} from "@/pages/talent/templates/TemplatesIndex.tsx";

export const talentRoutes: RouteConfig[] = [
  {
    path: "my-feedback",
    anyOfPermissions: [ApiPermission.performance_reviews_module],
    children: [
      {
        path: "",
        redirect: "/my-feedback/requests",
      },
      {
        path: ":section",
        element: <MyFeedbackIndex />,
      },
    ],
  },
  {
    path: "surveys",
    planFeature: ApiFeatureType.talent_surveys,
    allOfPermissions: [ApiPermission.surveys_module],
    children: [
      {
        path: "",
        element: <SurveysIndex />,
      },
      {
        path: "requests",
        element: <SurveyRequestsPage />,
      },
      {
        path: "history",
        element: <SurveyHistoryPage />,
      },
      {
        path: ":id/view/:step?",
        element: <SurveysView />,
      },
      {
        path: ":id/respond/:step?",
        element: <SurveyRespond />,
        isFullScreen: true,
      },
      {
        path: ":id/setup/:step?",
        allOfPermissions: [ApiPermission.surveys_module],
        element: <SurveyEditPage />,
        isFullScreen: true,
      },
    ],
  },
  {
    path: "performance-reviews",
    planFeature: ApiFeatureType.talent_performance_reviews,
    allOfPermissions: [ApiPermission.performance_reviews_module],
    children: [
      {
        path: "",
        element: <PerformanceReviewsIndex />,
      },
      {
        path: ":id/view/:section?",
        element: <PerformanceReviewViewPage />,
      },
      {
        path: ":id/view/participants/:employee_id",
        isFullScreen: true,
        children: [
          {
            path: ":tab?",
            element: <PerformanceReviewParticipantReport />,
          },
          {
            path: "type/:type_id",
            element: <PerformanceReviewParticipantReport />,
          },
        ],
      },
      {
        path: ":id/setup/:step?/:type_id?",
        element: <PerformanceReviewEditPage />,
        isFullScreen: true,
      },
      {
        path: "reviewers/:reviewer_id/respond",
        element: <PerformanceReviewRespond />,
        isFullScreen: true,
      },
    ],
  },
  {
    path: "templates",
    children: [
      {
        path: "",
        anyOfPermissions: [ApiPermission.surveys_module, ApiPermission.performance_reviews_module],
        anyOfViewPermissions: [
          ApiPermission.survey_templates,
          ApiPermission.performance_review_templates,
        ],
        element: <TemplatesIndexRedirect />,
      },
      {
        path: "surveys",
        planFeature: ApiFeatureType.talent_surveys,
        allOfPermissions: [ApiPermission.surveys_module],
        anyOfViewPermissions: [ApiPermission.survey_templates],
        children: [
          {
            path: "",
            element: <SurveyTemplatesIndex />,
          },
          {
            path: "all",
            element: <SurveyTemplatesIndex list />,
          },
          {
            path: "folders/:folderId",
            element: <SurveyTemplatesIndex list />,
          },
          {
            path: ":id/setup/:step?",
            allOfPermissions: [ApiPermission.survey_templates],
            element: <SurveyTemplateEditPage />,
            isFullScreen: true,
          },
        ],
      },
      {
        path: "performance-reviews",
        planFeature: ApiFeatureType.talent_performance_reviews,
        allOfPermissions: [ApiPermission.performance_reviews_module],
        anyOfViewPermissions: [ApiPermission.performance_review_templates],
        children: [
          {
            path: "",
            element: <PerformanceReviewTemplatesIndex />,
          },
          {
            path: "all",
            element: <PerformanceReviewTemplatesIndex list />,
          },
          {
            path: "folders/:folderId",
            element: <PerformanceReviewTemplatesIndex list />,
          },
          {
            path: ":id/setup/:step?",
            element: <PerformanceReviewTemplateEditPage />,
            isFullScreen: true,
          },
        ],
      },
    ],
  },
  {
    path: "goals",
    planFeature: ApiFeatureType.goals,
    allOfPermissions: [ApiPermission.goals_module],
    children: [
      {
        path: "",
        element: <InitiativesGoalsPage />,
      },
      {
        path: "initiatives",
        element: <InitiativesGoalsPage />,
      },
      {
        path: "strategic",
        children: [
          {
            path: "",
            element: <StrategicGoalsPage />,
          },
          {
            path: ":id",
            element: <StrategicGoalPage />,
          },
        ],
      },
      {
        path: "search",
        element: <SearchGoalsPage />,
      },
      {
        path: "personal",
        element: <SearchGoalsPage />,
      },
      {
        path: "department/:department_id",
        element: <SearchGoalsPage />,
      },
      {
        path: "team/:team_id",
        element: <SearchGoalsPage />,
      },
      {
        path: "reports/direct",
        element: <SearchGoalsPage />,
      },
      {
        path: "reports/subordinates",
        element: <SearchGoalsPage />,
      },
      {
        path: ":id",
        element: <GoalDetails />,
      },
    ],
  },
];
